import type { ArtifactShareRequest, ShareableArtifactType } from '../utils/artifactSharing';

import { fetchWithRetry as fetch, responseError } from './apiError';
import { tokenService } from './tokenService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface ArtifactShareInfo {
  id: string;
  project_id: string;
  share_id: string;
  artifact_type: ShareableArtifactType;
  item_id: string | null;
  subview: string | null;
  is_public: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface SharedArtifactItem {
  id: string;
  title: string;
  content: string;
  format: 'markdown' | 'mermaid' | 'html' | 'jsx' | 'task';
  status?: string;
  task_number?: number | null;
}

export interface SharedArtifactResponse {
  project_id: string;
  project_name: string;
  share_id: string;
  artifact_type: ShareableArtifactType;
  title: string;
  items: SharedArtifactItem[];
}

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${tokenService.getAccessToken()}`,
  'Content-Type': 'application/json',
});

const pendingShares = new Map<string, Promise<ArtifactShareInfo>>();

const sharePath = (projectId: string, shareId?: string): string => (
  `${API_BASE_URL}/api/v1/projects/${encodeURIComponent(projectId)}/artifact-shares`
  + (shareId ? `/${encodeURIComponent(shareId)}` : '')
);

async function createShare(request: ArtifactShareRequest): Promise<ArtifactShareInfo> {
  const response = await fetch(sharePath(request.projectId), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({
      artifact_type: request.artifactType, item_id: request.itemId ?? null, subview: request.subview ?? null,
    }),
  });

  if (!response.ok) throw await responseError(response);

  return response.json();
}

function createOrGetShare(request: ArtifactShareRequest): Promise<ArtifactShareInfo> {
  const key = JSON.stringify([tokenService.getAccessToken(), request.projectId,
    request.artifactType, request.itemId ?? null, request.subview ?? null]);
  const pending = pendingShares.get(key);

  if (pending) return pending;
  const result = createShare(request).finally(() => pendingShares.delete(key));

  pendingShares.set(key, result);

  return result;
}

async function toggleVisibility(share: ArtifactShareInfo, isPublic: boolean): Promise<ArtifactShareInfo> {
  const response = await fetch(sharePath(share.project_id, share.share_id), {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ is_public: isPublic }),
  });

  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function deleteShare(share: ArtifactShareInfo): Promise<void> {
  const response = await fetch(sharePath(share.project_id, share.share_id), {
    method: 'DELETE', headers: authHeaders(),
  });

  if (!response.ok) throw await responseError(response);
}

async function getSharedArtifact(shareId: string, authenticated = false): Promise<SharedArtifactResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/shared/artifacts/${encodeURIComponent(shareId)}${authenticated ? '/auth' : ''}`,
    { headers: authenticated ? authHeaders() : undefined },
  );

  if (!response.ok) throw await responseError(response);

  return response.json();
}

export const artifactShareService = { createOrGetShare, toggleVisibility, deleteShare, getSharedArtifact };
